import { LitElement, html, TemplateResult } from 'lit';
import { state } from 'lit/decorators.js';
import { styles } from '../view/styles';
import { EventDetailsPanel, XdrSearchResultsDetailsPanelEvents } from '../../xdrSearchResultsPanel/types';
import { GzSelectWebComponent } from '../../../ui/gzSelect';
import { SelectEvents } from '../../../ui/gzSelect/types';
import { flattenJson } from '../../../utils/formatter.utils';
import { FlexibleTableDataSource, FlexibleTableDisplayColumn } from '../../../ui/gzFlexibleTable/types';
import { EventDetail, EventType } from '../types';
import { GzCollapsableContainerWebComponent } from '../../../ui/gzCollapsableContainer';
import { ImageState } from '../../../ui/gzImageRepo/types';
import { copyToClipboard } from '../../../utils/htmlElement.utils';
import { GzTooltip } from '../../../ui/gzTooltip';
import { MultipleValueUtils } from '../../../ui/gzFlexibleTable/utils/multipleValueHelper';
import { GzLinkWebComponent } from '../../../ui/gzLinkComponent';
import { FontSizes } from '../../../types/common/types';
import { LinkColor, LinkType } from '../../../ui/gzLinkComponent/types';
import { htmlEncode } from '../../../utils/escape.utils';
import moment from 'moment';

export class XdrSearchEventDetailsPanelWebComponent extends LitElement {
  static get styles() {
    return styles;
  }
  private static readonly excludedFieldsFromTable = [
    'raw_data',
    'api_key',
    'ec_insert_date',
    'unique_id',
    'incident_id',
    'is_gz_demo',
    'demo_version',
  ];
  private static readonly attackTechniqueAndSubtechniqueFields = [
    'alert.att&ck_technique_id',
    'alert.att&ck_subtechnique_id',
  ];

  private static readonly MITRE_URL = 'https://attack.mitre.org/techniques';

  panelConfig: EventDetailsPanel;
  private dataSource: FlexibleTableDataSource<EventDetail>;
  private iconUp = 'icon-view_more_up';
  private iconDown = 'icon-view_more_down';

  private isXdrDemo = false;

  @state()
  private tooltipShown;

  connectedCallback(): void {
    super.connectedCallback();
    this.addEventListener('mouseleave', this.handleTooltipMouseLeave);
  }

  disconnectedCallback(): void {
    this.removeEventListener('mouseleave', this.handleTooltipMouseLeave);
    super.disconnectedCallback();
  }

  getActionColumn(field: string, value: string | Array<string>): TemplateResult {
    const component = new GzSelectWebComponent();
    component.setAttribute('part', 'select-icon');
    component.innerHTML = `<gz-icon-button
        icon="icon-add_search"
        buttonType="round"
        buttonFill="quiet"
        buttonColor="secondary"
        size="18"
        fontSize="8"></gz-icon-button>`;

    component.options = [
      {
        title: 'OR',
        value: 'OR',
      },
      {
        title: 'AND',
        value: 'AND',
      },
      {
        title: 'AND NOT',
        value: 'AND NOT',
      },
    ];

    const formattedField = this.formatField(field);

    component.addEventListener(SelectEvents.SELECTION_CHANGED, (e: CustomEvent) => {
      const selectedOption = e.detail.selected;
      const event = new CustomEvent(XdrSearchResultsDetailsPanelEvents.REFINE_SEARCH_FROM_SIDE_PANEL, {
        detail: {
          operator: selectedOption,
          field: formattedField,
          value: value,
        },
        bubbles: true,
        composed: true,
      });

      this.dispatchEvent(event);
    });
    return html`${component}`;
  }

  formatField(field: string): string {
    const subFields = field.split('.');
    subFields.forEach((item, index) => {
      if (!isNaN(Number(item))) {
        subFields.splice(index, 1);
      }
    });

    return subFields.join('.');
  }

  getIconByFieldType(fieldValue: string | number | Date): TemplateResult {
    const isTimestamp = moment(fieldValue, 'YYYY-MM-DDTHH:mm:ss+HH:mm', true).isValid();
    if (isTimestamp) {
      return html`<i part="table-row-icon" class="table-row-icon ds-icon-calendar"></i>`;
    }
    switch (typeof fieldValue) {
      case 'number':
        return html`<i part="table-row-icon" class="table-row-icon">#</i>`;
      case 'boolean':
        return html`<i part="table-row-icon" class="table-row-icon">01</i>`;
      case 'string':
      default:
        return html`<i part="table-row-icon" class="table-row-icon">t</i>`;
    }
  }

  protected render() {
    return html`
      <div class="details-panel-wrapper">
        <div class="details-panel">${this.renderHeader()} ${this.renderContent()}</div>
      </div>
    `;
  }

  private renderHeader(): TemplateResult {
    return html`
      <div class="details-panel-header">
        <gz-title class="title" fontweight="500" fontsize="16">${this.panelConfig.eventName}</gz-title>
      </div>
    `;
  }

  private renderContent(): TemplateResult {
    return html` ${this.renderGeneralSection()} ${this.renderDetailsSection()} `;
  }

  private getSeverity(value: number): { severityText: string; severityColor: string } {
    const severity = {
      severityText: '',
      severityColor: '',
    };

    if (value >= 80) {
      severity.severityText = this.panelConfig.severityHigh;
      severity.severityColor = 'bullet-danger';
    } else if (value >= 50) {
      severity.severityText = this.panelConfig.severityMedium;
      severity.severityColor = 'bullet-orange';
    } else {
      severity.severityText = this.panelConfig.severityLow;
      severity.severityColor = 'bullet-yellow';
    }

    return severity;
  }

  private renderGeneralSection(): GzCollapsableContainerWebComponent {
    //TODO
    const severityResults = this.getSeverity(this.panelConfig.severity);
    const buttonStrings = {
      showMore: this.panelConfig.showMore,
      showLess: this.panelConfig.showLess,
    };

    const collapsableContainer = new GzCollapsableContainerWebComponent();
    collapsableContainer.fixedContentHeight = 200;
    collapsableContainer.buttonStrings = buttonStrings;

    const generalLabaledContent = `
      <table>
        <gz-labeled-content label="${this.panelConfig.severityTitle}" labelfontweight="500" labelfontsize="12">
          <span class="has-bullet ${severityResults.severityColor}">${severityResults.severityText}</span>
        </gz-labeled-content>
        <gz-labeled-content label="${this.panelConfig.detectedOnTitle}" labelfontweight="500" labelfontsize="12">
          ${this.panelConfig.detectedOn}
        </gz-labeled-content>
        <gz-labeled-content label="${this.panelConfig.detectedByTitle}" labelfontweight="500" labelfontsize="12">
          ${this.panelConfig.detectedBy}
        </gz-labeled-content>
        <gz-labeled-content label="${this.panelConfig.typeTitle}" labelfontweight="500" labelfontsize="12">
          ${this.panelConfig.type}
        </gz-labeled-content>
      </table>
    `;

    collapsableContainer.innerHTML = generalLabaledContent;
    return collapsableContainer;
  }

  private renderDetailsSection(): TemplateResult {
    return html`
      <div class="details-panel-details-wrapper">
        <gz-labeled-content
          class="details-panel-content-title"
          label="${this.panelConfig.detailsTitle}"
          labelfontsize="11"
          labelfontweight="600"
        ></gz-labeled-content>
        <gz-tab-group class="xdr-search-event-details-panel-tab-group" id="xdr-search-event-details-panel-tab-group">
          ${this.renderTabs()}
        </gz-tab-group>
      </div>
    `;
  }

  private renderTabs(): TemplateResult {
    const tableTabComponent = html` <gz-tab label="${this.panelConfig.tableTitle}">
      ${this.renderFlexibleTable()}
    </gz-tab>`;

    const eventType = this.panelConfig.rawEvent?.['other']?.['event_type'];
    if (
      this.panelConfig.rawEvent.hasOwnProperty('other') &&
      (eventType === EventType.ALERT || eventType === EventType.XALERT)
    ) {
      const jsonTabComponent = html` <gz-tab label="${this.panelConfig.jsonTitle}"> ${this.renderJSON()}</gz-tab>`;

      return html`${tableTabComponent} ${jsonTabComponent}`;
    }

    return html`${tableTabComponent}`;
  }

  private renderFlexibleTable(): TemplateResult {
    const eventDetails = this.buildEventDetails();

    return html` <gz-flexible-table
      @rowClicked=${event => this.expandRow(event.detail.rowData, true)}
      .dataSource=${this.buildTableDataSource(eventDetails)}
    ></gz-flexible-table>`;
  }

  private renderJSON(): TemplateResult {
    const rawData = this.panelConfig.rawEvent['raw_data'];

    if (rawData === undefined || Object.keys(rawData).length === 0) {
      return html` <gz-image-repo state="${ImageState.EMPTY}">
        <gz-text class="empty-view" fontWeight="500" fontSize="16">${this.panelConfig.emptyJsonText}</gz-text>
      </gz-image-repo>`;
    }

    return html`
      <div class="mt-1 d-flex justify-end">
        <gz-button
          @click="${this.handleCopyButton}"
          class="confirmationButton"
          type="outline"
          color="primary"
          buttonHeight="small"
          buttonFontSize="small"
          lowercase="true"
          >${this.panelConfig.copyBtnString}
        </gz-button>
      </div>
      <gz-json-viewer .jsonData=${rawData}></gz-json-viewer>
    `;
  }

  private handleCopyButton(event: Event) {
    const rawData = this.panelConfig.rawEvent['raw_data'];

    copyToClipboard(JSON.stringify(rawData));
    this.handleTooltip(event);
  }

  private handleTooltip(event) {
    GzTooltip.show(this.panelConfig.confirmationCopy, event);

    this.tooltipShown = setTimeout(() => {
      GzTooltip.hide();
    }, 2000);
  }

  private handleTooltipMouseLeave(): void {
    clearTimeout(this.tooltipShown);
    GzTooltip.hide();
  }

  private buildEventDetails(): EventDetail[] {
    this.isXdrDemo = this.panelConfig.rawEvent['is_gz_demo'];
    const flatJson = flattenJson(
      this.panelConfig.rawEvent,
      XdrSearchEventDetailsPanelWebComponent.excludedFieldsFromTable,
      false
    );
    const eventDetails: EventDetail[] = [];

    for (const field of Object.keys(flatJson)) {
      eventDetails.push({
        field,
        value: flatJson[field],
      });
    }
    return eventDetails;
  }

  private buildTableDataSource(eventDetails: EventDetail[]): FlexibleTableDataSource<EventDetail> {
    this.dataSource = new FlexibleTableDataSource<EventDetail>(eventDetails);
    this.dataSource.setDisplayColumns(this.buildTableColumnsArray());

    this.dataSource.setAdditionalRowRenderer((rowData: EventDetail) => {
      if (rowData?.expanded && Array.isArray(rowData?.value) && rowData?.value.length > 1) {
        return html`
          ${(rowData.value as Array<string>)?.slice(1).map(
            source => html`
              <div class="flexible-table__row">
                <div class="flexible-table__cell"></div>
                <div class="flexible-table__cell flex-3"></div>
                <div class="flexible-table__cell flex-6">${this.generateFieldTemplate(source, rowData)}</div>
              </div>
            `
          )}
          <div class="icon-move-up" @click=${() => this.expandRow(rowData, false)}>
            ${MultipleValueUtils.getActionButtonTemplate(this.iconUp)}
          </div>
        `;
      }
      return html``;
    });

    this.dataSource.setRowClasses = function (rowData: EventDetail): string {
      if (rowData.expanded) {
        return '';
      }

      if (Array.isArray(rowData.value) && rowData?.value.length > 1) {
        return 'cursor-pointer';
      }

      return '';
    };

    return this.dataSource;
  }

  private generateFieldTemplate(source, rowData) {
    if (XdrSearchEventDetailsPanelWebComponent.attackTechniqueAndSubtechniqueFields.includes(rowData.field)) {
      return this.getValueTemplatesForAttackTechniqueAndSubtechnique(
        this.generateMitreLinkForPanel(source),
        this.getActionColumn(rowData.field, source)
      );
    }
    return MultipleValueUtils.getValueTemplate(JSON.stringify(source), this.getActionColumn(rowData.field, source));
  }

  private getValueTemplatesForAttackTechniqueAndSubtechnique(displayValue, actionTemplate, valueCounter?) {
    return html`
      <span class="multiple-value-container">
        <div class="multiple-value__text">${displayValue}</div>
        ${valueCounter}
        <span class="action-template-container"> ${actionTemplate} </span>
      </span>
    `;
  }

  private buildTableColumnsArray(): FlexibleTableDisplayColumn[] {
    const tableColumns: FlexibleTableDisplayColumn[] = [];

    tableColumns.push(
      new FlexibleTableDisplayColumn()
        .withSideHeader(true)
        .withCellRenderer(rowData => this.getIconByFieldType(rowData.value))
    );
    tableColumns.push(new FlexibleTableDisplayColumn().withFlex('3').withRenderPath('field'));
    tableColumns.push(
      new FlexibleTableDisplayColumn().withFlex('6').withCellRenderer(this.renderFieldColumnTemplate.bind(this))
    );

    return tableColumns;
  }

  private expandRow(data, expanded): void {
    if (!(Array.isArray(data.value) && data.value.length)) {
      return;
    }
    if (data.expanded !== expanded) {
      data.expanded = expanded;
      this.dataSource.requestUpdate();
    }
  }

  private renderFieldColumnTemplate(rowData: EventDetail): TemplateResult {
    if (this.isXdrDemo && rowData.field === 'alert.incident_number') {
      rowData.value = 'DEMO';
    }

    if (Array.isArray(rowData.value) && rowData.value.length) {
      if (rowData.value.length === 1) {
        if (XdrSearchEventDetailsPanelWebComponent.attackTechniqueAndSubtechniqueFields.includes(rowData.field)) {
          return html`
            <div part="clip-cell">
              <gz-clip-content placeholderPadding>
                ${this.generateMitreLinkForPanel(rowData.value?.[0])}</gz-clip-content
              >
              ${this.getActionColumn(rowData.field, rowData.value?.[0])}
            </div>
          `;
        }
        return html`
          <div part="clip-cell">
            <gz-clip-content placeholderPadding> ${JSON.stringify(rowData.value?.[0])}</gz-clip-content>
            ${this.getActionColumn(rowData.field, rowData.value?.[0])}
          </div>
        `;
      }

      const actionTemplate = !rowData.expanded
        ? MultipleValueUtils.getActionButtonTemplate(this.iconDown)
        : this.getActionColumn(rowData.field, rowData.value[0]);
      const counterTemplate = !rowData.expanded
        ? MultipleValueUtils.getMultipleValueCounter(rowData.value?.length - 1)
        : '';

      if (XdrSearchEventDetailsPanelWebComponent.attackTechniqueAndSubtechniqueFields.includes(rowData.field)) {
        return this.getValueTemplatesForAttackTechniqueAndSubtechnique(
          this.generateMitreLinkForPanel(rowData.value?.[0]),
          actionTemplate,
          counterTemplate
        );
      }
      return html`
        ${MultipleValueUtils.getValueTemplate(JSON.stringify(rowData.value?.[0]), actionTemplate, counterTemplate)}
      `;
    }

    if (this.panelConfig.searchableFields.indexOf(this.formatField(rowData.field)) === -1) {
      return html` <div part="clip-cell">
        <gz-clip-content placeholderPadding> ${JSON.stringify(rowData.value)}</gz-clip-content>
      </div>`;
    }

    return html`
      <div part="clip-cell">
        <gz-clip-content placeholderPadding> ${JSON.stringify(rowData.value)}</gz-clip-content>
        ${this.getActionColumn(rowData.field, rowData.value)}
      </div>
    `;
  }

  generateMitreLinkForPanel(techniqueId: string): GzLinkWebComponent {
    const mitreLink = new GzLinkWebComponent();
    mitreLink.fontSize = FontSizes.PARAGRAPH_12;
    mitreLink.color = LinkColor.PRIMARY;
    mitreLink.showIcon = false;
    mitreLink.innerHTML = htmlEncode(techniqueId);
    techniqueId = techniqueId.replace('.', '/');
    mitreLink.href = `${XdrSearchEventDetailsPanelWebComponent.MITRE_URL}/${techniqueId}`;
    mitreLink.type = LinkType.QUIET;

    return mitreLink;
  }
}
